import { useState, useEffect } from 'react';
import TextSelect from 'ui_system/select/TextSelect';
import Validator from 'validate.js';
import { setFormContraints } from 'views/guest_information/Helpers';
import { capitalize } from 'lib/string_utils';
import PhoneInput from 'views/ui/form_elements/input/phone';
import {
  AddGuestFormWrapper,
  AddFormInput,
  AddFormHeading,
  Label,
  InputColumn,
  Input,
  Footer,
  Error,
  SaveButton,
  DrowDown,
  TicketHeading,
} from './GuestForm.styles';
import { GuestFormProps } from './MultiSelectGuestOption';

const GuestForm = ({
  onAddGuest,
  onEditGuest,
  onUpdateGuestDetail,
  units,
  country,
  type,
  guest,
}: GuestFormProps) => {
  const [errors, setErrors] = useState<any[]>([]);
  // If type is not edit then isDisabled will be true else false
  const isDisabled = type !== 'edit';
  const [disableSubmit, setDisableSubmit] = useState(isDisabled);

  const [fields, setFields] = useState({
    firstName: guest?.firstName,
    lastName: guest?.lastName,
    email: guest?.email,
    phone: guest?.phone,
    ticketUnitType: guest?.ticketUnitType,
    ticketUniqId: guest?.ticketUniqId,
  });

  const { firstName, lastName, email, phone, ticketUnitType } = fields;
  const [selectedTicket, setSelectedTicket] = useState<string | null>(
    ticketUnitType || null
  );

  // Enable submit button if there's only one ticket type
  useEffect(() => {
    if (units.length === 1) {
      const singleTicket = capitalize(units[0].noun_singular);
      setSelectedTicket(singleTicket);
      setDisableSubmit(false);
    }
  }, [units]);

  const _errorsForInput = (inputName: any) => {
    if (errors?.[inputName]) {
      return (
        <Error data-testid={`${inputName}-error`}>
          {errors[inputName].join(', ')}
        </Error>
      );
    }
    return '';
  };

  const _inputChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setFields({
      ...fields,
      [e.target.name]: value,
    });
  };

  const _phoneInputProps = () => {
    return {
      type: 'tel',
      name: 'phone',
      value: fields.phone,
      onChange: (name: string, value: string) => {
        setFields({ ...fields, [name]: value });
      },
      defaultCountry: country ? country.toLowerCase() : 'us',
    };
  };

  const _validate = (constraints: any) => {
    const validationErrors =
      Validator(fields, constraints, { fullMessages: false }) || {};
    setErrors({ ...validationErrors });
    return !Object.keys(validationErrors).length;
  };

  const _submitForm = async () => {
    const guestData = {
      firstName,
      lastName,
      phone,
      email,
      ticketUnitType: selectedTicket,
      unitTypeId: units.find(
        (u: any) =>
          u.noun_singular.toLowerCase() === selectedTicket?.toLowerCase()
      ).id,
      uiId: '',
    };
    onUpdateGuestDetail();
    if (type === 'add') {
      onAddGuest(guestData);
    } else {
      guestData.uiId = guest.uiId;
      onEditGuest(guestData);
    }
  };

  const _submitClicked = (e: React.FormEvent) => {
    e.preventDefault();
    const constraints = setFormContraints(
      fields,
      firstName,
      lastName,
      'AddGuest'
    );
    if (_validate(constraints)) {
      _submitForm();
    }
  };

  const onItemChange = (item: any) => {
    setSelectedTicket(item);
    setDisableSubmit(false);
  };

  const formatSelectOptions = (ticketUnits: any) => {
    // eslint-disable-next-line camelcase
    return ticketUnits.map((unit: { noun_singular: any }) => {
      return {
        label: capitalize(unit.noun_singular),
        value: capitalize(unit.noun_singular),
      };
    });
  };

  const titleText = () => {
    if (type === 'add') {
      return 'Add Guest';
    }
    return 'Edit Guest';
  };

  return (
    <AddGuestFormWrapper data-testid="add-guest-form">
      <AddFormInput>
        <AddFormHeading data-testid="title">{titleText()}</AddFormHeading>
        {type !== 'add' && (
          <TicketHeading data-testid="unique-ticket-id">
            {fields.ticketUniqId ? `ID: ${fields.ticketUniqId}` : 'n/a'}
          </TicketHeading>
        )}
      </AddFormInput>
      <AddFormInput data-testid="ticket-type">
        <Label>Ticket type</Label>
        <DrowDown>
          <TextSelect
            dataTestId="select-box"
            customDropdown={false}
            selectedLabel={selectedTicket}
            selectedValue={selectedTicket}
            onSelectItem={onItemChange}
            items={formatSelectOptions(units)}
            isOpenable={units.length > 1}
            flexibleHeight
            brandColor={undefined}
            unitsSelectOptions={undefined}
            type={type}
          />
        </DrowDown>
      </AddFormInput>
      <AddFormInput data-testid="name-wrap">
        <Label>Name (Optional)</Label>
        <InputColumn>
          <Input
            data-testid="first-name"
            placeholder="First"
            name="firstName"
            onChange={_inputChanged}
            value={firstName}
          />
          {_errorsForInput('firstName')}
        </InputColumn>
        <InputColumn>
          <Input
            data-testid="last-name"
            placeholder="Last"
            name="lastName"
            onChange={_inputChanged}
            value={lastName}
          />
          {_errorsForInput('lastName')}
        </InputColumn>
      </AddFormInput>
      <AddFormInput data-testid="email-wrap">
        <Label>Email address (Optional)</Label>
        <Input
          data-testid="email"
          placeholder="email@example.com"
          name="email"
          onChange={_inputChanged}
          value={email}
        />
        {_errorsForInput('email')}
      </AddFormInput>
      <AddFormInput data-testid="phone-wrap">
        <Label>Phone (Optional)</Label>
        <PhoneInput {..._phoneInputProps()} />
        {_errorsForInput('phone')}
      </AddFormInput>
      <Footer data-testid="footer">
        <SaveButton
          data-testid="save-btn"
          onClick={_submitClicked}
          theme="green"
          disabled={disableSubmit}
        >
          {type === 'add' ? 'Add' : 'Apply changes'}
        </SaveButton>
      </Footer>
    </AddGuestFormWrapper>
  );
};

export default GuestForm;
